import { ChangeDetectorRef, Component } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { IonicModule } from '@ionic/angular';
import {
	VehicleRoadTaxStatusEnum,
	VehicleRoadTaxListDto,
	VehicleRoadTaxRecordDto,
	VehicleRoadTaxesAppOptionsDto,
	VehicleRoadTaxSearchDto,
	VehicleRoadTaxesFilters,
	VehicleRoadTaxesStatsDto,
	vehicleRoadTaxValidators,
	vehicleRoadTaxStatusToColor,
	VehicleRoadTaxRecordCategoryEnum,
	CreateVehicleRoadTaxDto,
	UpdateVehicleRoadTaxDto,
	ModulesEnum,
	TagRecordCategoryEnum,
} from '@splitsecondconnect/ultramarine-shared';

import { RecordTabsComponent } from '../../../../layout/components/record-tabs/record-tabs.component';
import { TabContentDirective } from '../../../../layout/components/record-tabs/tab-content.directive';
import { BaseRecordComponent } from '../../../../shared/base-classes/base-record.component';
import { HeaderDataModel } from '../../../../shared/components/headers/record-header/record-header.model';
import { ModuleSelectorComponent } from '../../../../shared/components/module-selector/module-selector.component';
import { MoreDetailsComponent } from '../../../../shared/components/more-details/more-details.component';
import { NoActivityComponent } from '../../../../shared/components/no-activity.component';
import { AnnotationsSectionComponent } from '../../../../shared/components/annotations-section/annotations-section.component';
import { ADMINISTRATION_RECORD_TABS } from '../../../../shared/enums/administration-record-tabs';
import { dateCast } from '../../../../shared/functions/sectionCasters.function';
import {
	mapBaseDtoToBaseModel,
	mapBaseModelToBaseDto,
} from '../../../../shared/mapper/base.mapper';
import {
	mapFilesDtoToModel,
	mapFilesModelToDto,
} from '../../../../shared/mapper/files.mapper';
import {
	mapTagDtoToModel,
	mapTagModelToDto,
} from '../../../../shared/mapper/tags.mapper';
import { MODULE_SELECTOR_STRATEGY_TOKEN } from '../../../../shared/tokens/module-selector.token';
import { getTransactionFilters } from '../../transactions/config/transaction-filters.config';
import { getVehicleMaintenanceFilters } from '../../vehicle-maintenances/config/vehicle-maintenance-filters.config';
import { VehicleSectionComponent } from '../../vehicles/sections/vehicle-section/vehicle-section.component';
import { VehicleRoadTaxesListItemMenuComponent } from '../components/vehicle-road-taxes-list-item-menu/vehicle-road-taxes-list-item-menu.component';
import { VehicleRoadtaxModuleSelectorStrategy } from '../config/vehicle-road-tax-module-selector-strategy';
import { VehicleRoadTaxRecordModel } from '../models/vehicle-road-tax-record.model';
import { VehicleRoadTaxesService } from '../vehicle-road-taxes.service';

import { VehicleRoadTaxesDetailsComponent } from './components/vehcile-road-taxes-details/vehicle-road-taxes-details.component';
import { VehicleRoadTaxesSummaryComponent } from './components/vehcile-road-taxes-summary/vehicle-road-taxes-summary.component';
import { TAGS_RECORD_CATEGORY_TOKEN } from '../../../../shared/tokens/tags-record-category.token';
import { CommentsSectionComponent } from 'app/shared/components/comments-section/comments-section.component';
import { StepperComponent } from '../../../../shared/components/stepper/stepper.component';
import { SectionMetadataDirective } from '../../../../shared/directives/section-metadata.directive';
import { ContextInfoComponent } from '../../../../shared/components/context-info/context-info.component';
import { ErrorsBoxComponent } from '../../../../shared/components/errors-box/errors-box.component';
import { UltraPrimaryButtonComponent } from '../../../../shared/components/ultramarine-ui/ultra-primary/ultra-primary.component';
import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import { CommentsCardComponent } from '../../../../shared/components/comments-card/comments-card.component';
import { RecordHeaderComponent } from '../../../../shared/components/headers/record-header/record-header.component';
import { RecordFooterComponent } from '../../../../shared/components/footers/record-footer/record-footer.component';

@Component({
	selector: 'app-vehicle-road-taxes-record',
	templateUrl: 'vehicle-road-taxes-record.component.html',
	host: {
		'[class]': 'modalClass()',
	},
	providers: [
		{
			provide: MODULE_SELECTOR_STRATEGY_TOKEN,
			useClass: VehicleRoadtaxModuleSelectorStrategy,
		},
		{
			provide: TAGS_RECORD_CATEGORY_TOKEN,
			useValue: TagRecordCategoryEnum.VEHICLE_ROAD_TAX,
		},
	],
	imports: [
		VehicleRoadTaxesSummaryComponent,
		VehicleRoadTaxesDetailsComponent,
		VehicleSectionComponent,
		VehicleRoadTaxesListItemMenuComponent,
		AnnotationsSectionComponent,
		RecordTabsComponent,
		RecordHeaderComponent,
		RecordFooterComponent,
		AsyncPipe,
		TabContentDirective,
		IonicModule,
		ModuleSelectorComponent,
		MoreDetailsComponent,
		NoActivityComponent,
		CommentsSectionComponent,
		StepperComponent,
		SectionMetadataDirective,
		ContextInfoComponent,
		ErrorsBoxComponent,
		UltraPrimaryButtonComponent,
		NgTemplateOutlet,
		CommentsCardComponent,
	],
})
export class VehicleRoadTaxesRecordComponent extends BaseRecordComponent<
	VehicleRoadTaxRecordDto,
	CreateVehicleRoadTaxDto,
	UpdateVehicleRoadTaxDto,
	VehicleRoadTaxListDto,
	VehicleRoadTaxesStatsDto,
	VehicleRoadTaxesAppOptionsDto,
	VehicleRoadTaxesFilters,
	VehicleRoadTaxRecordModel,
	VehicleRoadTaxStatusEnum,
	VehicleRoadTaxSearchDto
> {
	constructor(
		private _vehicleRoadTaxesService: VehicleRoadTaxesService,
		private _activatedRoute: ActivatedRoute,
		private _router: Router,
		private _changeDetectorRef: ChangeDetectorRef,
	) {
		super(
			_vehicleRoadTaxesService,
			_router,
			_activatedRoute,
			_changeDetectorRef,
		);
		this.module = ModulesEnum.VEHICLE_ROAD_TAXES;
	}

	recordTabs = ADMINISTRATION_RECORD_TABS;

	recordCategories = VehicleRoadTaxRecordCategoryEnum;

	transactionFilters = getTransactionFilters().filter(
		(item) => item.module !== ModulesEnum.VEHICLE_ROAD_TAXES,
	);
	vehicleMaintenanceFilters = getVehicleMaintenanceFilters().filter(
		(item) => item.module !== ModulesEnum.VEHICLE_ROAD_TAXES,
	);

	protected _isRecordFormValid(): boolean {
		const validationResult = vehicleRoadTaxValidators.canCreateOrUpdate(
			this._mapModelToDto(this.recordData()),
		);
		this.errorMessages.next(validationResult.errors);
		return validationResult.isValid;
	}

	protected _updateHeaderData(
		record: VehicleRoadTaxRecordModel,
	): HeaderDataModel<VehicleRoadTaxStatusEnum> {
		return {
			...this._updateHeaderDataBase(record),
			title: record.code,
			status: record.status,

			color: vehicleRoadTaxStatusToColor(record.status),
		};
	}

	protected _mapDtoToModel(
		dto: VehicleRoadTaxRecordDto,
	): VehicleRoadTaxRecordModel {
		return {
			...mapBaseDtoToBaseModel(dto),
			code: dto.code ?? null,
			notes: dto.notes ?? null,
			externalReference: dto.externalReference ?? null,
			startsAt: dateCast(dto.startsAt),
			endsAt: dateCast(dto.endsAt),
			status: dto.status ?? null,
			vehicleId: dto.vehicleId ?? null,

			taxRateType: dto.taxRateType ?? null,
			taxRate: dto.taxRate ?? null,
			isTaxIncluded: dto.isTaxIncluded ?? null,
			amount: dto.amount ?? null,
			totalNet: dto.totalNet ?? null,
			totalTax: dto.totalTax ?? null,
			totalGross: dto.totalGross ?? null,
			recordCategory: dto.recordCategory ?? null,
			...mapTagDtoToModel(dto.tags),
			...mapFilesDtoToModel(dto.files),
		};
	}

	protected _mapModelToDto(
		model: VehicleRoadTaxRecordModel,
	): VehicleRoadTaxRecordDto {
		return {
			...mapBaseModelToBaseDto(model),
			code: model.code ?? null,
			notes: model.notes ?? null,
			externalReference: model.externalReference ?? null,
			startsAt: dateCast(model.startsAt) ?? null,
			endsAt: dateCast(model.endsAt) ?? null,
			vehicleId: model.vehicleId ?? null,

			taxRateType: model.taxRateType ?? null,
			taxRate: model.taxRate ?? null,
			isTaxIncluded: model.isTaxIncluded ?? null,
			amount: model.amount ?? null,
			totalNet: model.totalNet ?? null,
			totalTax: model.totalTax ?? null,
			totalGross: model.totalGross ?? null,
			recordCategory: model.recordCategory ?? null,
			...mapTagModelToDto(model.tags),
			...mapFilesModelToDto(model.files),
			status: model.status ?? null,
		};
	}

	protected _mapModelToCreateDto(
		model: VehicleRoadTaxRecordModel,
	): CreateVehicleRoadTaxDto {
		return {
			...mapBaseModelToBaseDto(model),
			code: model.code ?? null,
			notes: model.notes ?? null,
			externalReference: model.externalReference ?? null,
			startsAt: dateCast(model.startsAt) ?? null,
			endsAt: dateCast(model.endsAt) ?? null,
			vehicleId: model.vehicleId ?? null,

			taxRateType: model.taxRateType ?? null,
			taxRate: model.taxRate ?? null,
			isTaxIncluded: model.isTaxIncluded ?? null,
			amount: model.amount ?? null,
			totalNet: model.totalNet ?? null,
			totalTax: model.totalTax ?? null,
			totalGross: model.totalGross ?? null,
			recordCategory: model.recordCategory ?? null,
			...mapTagModelToDto(model.tags),
			...mapFilesModelToDto(model.files),
		};
	}

	protected _mapModelToUpdateDto(
		model: VehicleRoadTaxRecordModel,
	): UpdateVehicleRoadTaxDto {
		return {
			...mapBaseModelToBaseDto(model),
			code: model.code ?? null,
			notes: model.notes ?? null,
			externalReference: model.externalReference ?? null,
			startsAt: dateCast(model.startsAt) ?? null,
			endsAt: dateCast(model.endsAt) ?? null,
			vehicleId: model.vehicleId ?? null,

			taxRateType: model.taxRateType ?? null,
			taxRate: model.taxRate ?? null,
			isTaxIncluded: model.isTaxIncluded ?? null,
			amount: model.amount ?? null,
			totalNet: model.totalNet ?? null,
			totalTax: model.totalTax ?? null,
			totalGross: model.totalGross ?? null,
			recordCategory: model.recordCategory ?? null,
			...mapTagModelToDto(model.tags),
			...mapFilesModelToDto(model.files),
		};
	}
}
